import { useRef, useState, ChangeEvent } from 'react';

import { Button } from '@components/Button';

interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSizeMB?: number;
  onFilesChange: (files: File[]) => void;
  error?: string;
  currentFiles?: string[]; // URLs of already uploaded files
}

export function FileUpload({
  label = 'Upload files',
  accept = 'image/*',
  multiple = false,
  maxFiles = 5,
  maxSizeMB = 10,
  onFilesChange,
  error,
  currentFiles = [],
}: FileUploadProps): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files || []);
    setUploadError(null);

    // Validate file count
    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > maxFiles) {
      setUploadError(`Maximum ${maxFiles} file${maxFiles !== 1 ? 's' : ''} allowed`);
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(
      (file) => file.size > maxSizeMB * 1024 * 1024
    );
    if (oversizedFiles.length > 0) {
      setUploadError(
        `Some files exceed ${maxSizeMB}MB limit. Please select smaller files.`
      );
      return;
    }

    // Create previews for images
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    const newPreviews = imageFiles.map((file) => URL.createObjectURL(file));

    setSelectedFiles((prev) => [...prev, ...files]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    onFilesChange([...selectedFiles, ...files]);
  };

  const handleRemoveFile = (index: number): void => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);

    // Revoke object URLs to prevent memory leaks
    if (previews[index]) {
      URL.revokeObjectURL(previews[index]);
    }

    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
    onFilesChange(newFiles);
  };

  const handleRemoveCurrentFile = (_index: number): void => {
    // For already uploaded files, we'd need to delete from storage
    // This is a placeholder - actual implementation would call deleteFile
    // const newCurrentFiles = currentFiles.filter((_, i) => i !== index);
    // You'd need to pass this back to parent component
  };

  const displayError = error || uploadError;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
          {multiple && (
            <span className="text-gray-500 dark:text-gray-400 ml-1">
              (up to {maxFiles} files, max {maxSizeMB}MB each)
            </span>
          )}
        </label>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="space-y-4">
        {/* Current uploaded files */}
        {currentFiles.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentFiles.map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveCurrentFile(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Selected files preview */}
        {previews.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload button */}
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          fullWidth
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {selectedFiles.length > 0
            ? `Add More Files (${selectedFiles.length}/${maxFiles})`
            : 'Select Files'}
        </Button>

        {displayError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {displayError}
          </p>
        )}
      </div>
    </div>
  );
}

